import { useEffect, useId, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Send } from 'lucide-react';
import Button from '../../components/base/Button';
import Input from '../../components/base/Input';
import SelectMenu from '../../components/base/SelectMenu';
import SaudiRiyalSymbol from '../../components/base/icons/SaudiRiyalSymbol';
import InlineAlert from '../../components/common/InlineAlert';
import TextArea from '../../components/common/TextArea';
import { ACCOUNT_SUSPENDED_MESSAGE_KEY, apiErrorInfo, isAccountSuspended } from '../../utils/apiErrorInfo';
import { toLatinDigits } from '../../utils/arabicNormalize';
import {
  ENGAGEMENT_TYPES,
  createEngagement,
  type ServiceEngagement,
  type ServiceEngagementType,
} from '../../services/serviceEngagementService';
import { ENGAGEMENT_ICONS, PROVIDER_ID_RE } from './engagementMeta';
import ProviderField from './ProviderField';

/** The backend's limits (routes/service-engagements.js createSchema). */
const TITLE_MIN = 3;
const TITLE_MAX = 200;
const DESCRIPTION_MAX = 4000;
/** Whole riyals; nine digits is far above any real fee and inside a safe integer. */
const FEE_MAX_DIGITS = 9;

const LABEL = 'mb-1.5 block text-[0.875rem] font-medium text-[var(--pv-color-text)]';
const hintClass = (error: boolean) =>
  `mt-1.5 text-[0.875rem] leading-relaxed ${
    error ? 'text-[#C62828] dark:text-[var(--pv-color-red)]' : 'text-[var(--pv-color-text-muted)]'
  }`;

type FieldErrors = { type?: string; provider?: string; title?: string };

export interface RequestServiceFormProps {
  token: string;
  onSent: (engagement: ServiceEngagement) => void;
  onCancel: () => void;
}

/**
 * «طلب خدمة جديد»: the service, the professional, a title, an optional fee and
 * details. Checked on submit: the first field that fails takes focus and its
 * hint turns into the reason.
 */
export default function RequestServiceForm({ token, onSent, onCancel }: RequestServiceFormProps) {
  const { t } = useTranslation(['engage', 'common']);
  const uid = useId();
  const typeId = `${uid}-type`;
  const ids = {
    heading: `${uid}-heading`,
    type: typeId,
    typeError: `${uid}-type-error`,
    provider: `${uid}-provider`,
    title: `${uid}-title`,
    titleHint: `${uid}-title-hint`,
    fee: `${uid}-fee`,
    feeHint: `${uid}-fee-hint`,
    description: `${uid}-description`,
  };

  const [type, setType] = useState<ServiceEngagementType | ''>('');
  const [provider, setProvider] = useState('');
  const [title, setTitle] = useState('');
  const [fee, setFee] = useState('');
  const [description, setDescription] = useState('');
  // i18n keys, so a language switch re-renders the messages too.
  const [errors, setErrors] = useState<FieldErrors>({});
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    document.getElementById(typeId)?.focus();
  }, [typeId]);

  const clearError = (field: keyof FieldErrors) =>
    setErrors((prev) => (prev[field] ? { ...prev, [field]: undefined } : prev));

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (busy) return;

    const next: FieldErrors = {};
    if (!type) next.type = 'form.typeRequired';
    if (!provider) next.provider = 'provider.required';
    else if (!PROVIDER_ID_RE.test(provider)) next.provider = 'provider.idInvalid';
    if (title.trim().length < TITLE_MIN) next.title = 'form.titleTooShort';
    setErrors(next);
    const firstInvalid = next.type ? ids.type : next.provider ? ids.provider : next.title ? ids.title : null;
    if (firstInvalid) {
      document.getElementById(firstInvalid)?.focus();
      return;
    }
    if (!type) return; // narrows the type; validation above already caught it

    setBusy(true);
    setSubmitError(null);
    try {
      const created = await createEngagement(token, {
        type,
        providerActivityId: provider,
        title: title.trim(),
        description: description.trim() || undefined,
        feeAmount: fee ? Number(fee) : undefined,
      });
      onSent(created);
    } catch (error) {
      const { status } = apiErrorInfo(error);
      setSubmitError(
        isAccountSuspended(error)
          ? ACCOUNT_SUSPENDED_MESSAGE_KEY
          : status === 404
            ? 'errors.noProvider'
            : status === 400
              ? 'errors.invalid'
              : status === 401
                ? 'errors.session'
                : 'errors.create'
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <section
      aria-labelledby={ids.heading}
      className="mt-6 rounded-2xl border border-[var(--pv-border-soft)] bg-[var(--pv-color-surface-strong)] p-5 shadow-brand-xs sm:p-6"
    >
      <h2 id={ids.heading} className="text-lg font-bold text-[var(--pv-color-primary)]">
        {t('form.heading')}
      </h2>
      <form noValidate onSubmit={submit} className="mt-4 space-y-4">
        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <SelectMenu<ServiceEngagementType | ''>
              id={ids.type}
              label={t('form.type')}
              value={type}
              onChange={(next) => {
                setType(next);
                setProvider('');
                clearError('type');
                clearError('provider');
              }}
              options={ENGAGEMENT_TYPES.map((value) => ({
                value,
                label: t(`types.${value}`),
                icon: <i className={ENGAGEMENT_ICONS[value]} />,
              }))}
              placeholder={t('form.typePlaceholder')}
              invalid={Boolean(errors.type)}
              aria-describedby={errors.type ? ids.typeError : undefined}
            />
            {errors.type ? (
              <p id={ids.typeError} className={hintClass(true)}>
                {t(errors.type)}
              </p>
            ) : null}
          </div>

          <ProviderField
            key={type || 'none'}
            id={ids.provider}
            type={type}
            value={provider}
            onChange={(next) => {
              setProvider(next);
              clearError('provider');
            }}
            error={errors.provider}
          />
        </div>

        <div>
          <label htmlFor={ids.title} className={LABEL}>
            {t('form.title')}
          </label>
          <Input
            id={ids.title}
            value={title}
            onChange={(event) => {
              setTitle(event.target.value);
              clearError('title');
            }}
            required
            maxLength={TITLE_MAX}
            autoComplete="off"
            placeholder={t('form.titlePlaceholder')}
            invalid={Boolean(errors.title)}
            aria-invalid={errors.title ? true : undefined}
            aria-describedby={ids.titleHint}
          />
          <p id={ids.titleHint} className={hintClass(Boolean(errors.title))}>
            {t(errors.title ?? 'form.titleHint')}
          </p>
        </div>

        <div>
          <label htmlFor={ids.fee} className={LABEL}>
            {t('form.fee')}
          </label>
          <Input
            id={ids.fee}
            value={fee}
            onChange={(event) =>
              setFee(toLatinDigits(event.target.value).replace(/\D/g, '').slice(0, FEE_MAX_DIGITS))
            }
            inputMode="numeric"
            autoComplete="off"
            aria-describedby={ids.feeHint}
            endAdornment={<SaudiRiyalSymbol title="" className="h-5 w-5" />}
          />
          <p id={ids.feeHint} className={hintClass(false)}>
            {t('form.feeHint')}
          </p>
        </div>

        <div>
          <label htmlFor={ids.description} className={LABEL}>
            {t('form.description')}
          </label>
          <TextArea
            id={ids.description}
            value={description}
            onChange={(event) => setDescription(event.target.value)}
            rows={4}
            maxLength={DESCRIPTION_MAX}
            placeholder={t('form.descriptionPlaceholder')}
          />
        </div>

        {submitError ? <InlineAlert live="alert">{t(submitError)}</InlineAlert> : null}

        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <Button variant="secondary" onClick={onCancel} disabled={busy}>
            {t('cancel')}
          </Button>
          <Button type="submit" loading={busy}>
            {!busy ? <Send className="h-5 w-5 shrink-0 rtl:-scale-x-100" aria-hidden="true" /> : null}
            {t('form.submit')}
          </Button>
        </div>
      </form>
    </section>
  );
}
